import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import TaleButton from "@components/TaleButton";
import { PaymentProofHistory } from "@components/PaymentProof/PaymentProofHistory";
import { invoicesService } from "@/api/invoices";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * Admin side of a bank transfer: the proofs the client sent, with accept (marks the invoice paid)
 * or reject (with a reason the client sees, so they can send a new proof) on the one under review.
 */
export const PaymentProofReview = ({ invoice, onReviewed }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [rejectingId, setRejectingId] = useState(null);
    const [reason, setReason] = useState("");

    const { data: proofs = [], error } = useQuery({
        queryKey: ["payment-proofs", invoice.id],
        queryFn: () => invoicesService.getPaymentProofs(invoice.id),
        retry: false,
    });

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ["payment-proofs", invoice.id] });
        queryClient.invalidateQueries({ queryKey: ["invoices"] });
        onReviewed?.();
    };

    const showError = (err) => toast.error(err?.response?.data?.message || t("invoices.proof.reviewError"));

    const accept = useMutation({
        mutationFn: (proofId) => invoicesService.acceptPaymentProof(proofId),
        onSuccess: () => {
            toast.success(t("invoices.proof.acceptSuccess"));
            refresh();
        },
        onError: showError,
    });

    const reject = useMutation({
        mutationFn: ({ proofId, reason }) => invoicesService.rejectPaymentProof(proofId, reason),
        onSuccess: () => {
            toast.success(t("invoices.proof.rejectSuccess"));
            setRejectingId(null);
            setReason("");
            refresh();
        },
        onError: showError,
    });

    const handleAccept = (proofId) => {
        if (window.confirm(t("invoices.proof.acceptConfirm"))) {
            accept.mutate(proofId);
        }
    };

    const handleConfirmReject = (proofId) => {
        if (!reason.trim()) {
            toast.error(t("invoices.proof.reasonRequired"));
            return;
        }
        reject.mutate({ proofId, reason: reason.trim() });
    };

    const renderActions = (proof) => {
        if (proof.status !== "PENDING") return null;

        if (rejectingId === proof.id) {
            return (
                <div className="mt-4 space-y-2">
                    <textarea
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t("invoices.proof.rejectReasonPlaceholder")}
                        autoFocus
                    />
                    <div className="flex flex-wrap gap-2">
                        <TaleButton
                            variant="outlined"
                            className="!bg-red-50 !text-red-700"
                            onClick={() => handleConfirmReject(proof.id)}
                            loading={reject.isPending}
                            disabled={reject.isPending}
                        >
                            {t("invoices.proof.confirmReject")}
                        </TaleButton>
                        <TaleButton
                            variant="outlined"
                            onClick={() => {
                                setRejectingId(null);
                                setReason("");
                            }}
                        >
                            {t("common.cancel")}
                        </TaleButton>
                    </div>
                </div>
            );
        }

        return (
            <div className="mt-4 flex flex-wrap gap-2">
                {invoice.status === "SUBMITTED" && (
                    <TaleButton
                        variant="outlined"
                        className="!bg-green-50 !text-green-700"
                        onClick={() => handleAccept(proof.id)}
                        loading={accept.isPending}
                        disabled={accept.isPending}
                    >
                        <i className="fas fa-check-circle mr-2 rtl:mr-0 rtl:ml-2"></i>
                        {t("invoices.proof.accept")}
                    </TaleButton>
                )}
                <TaleButton
                    variant="outlined"
                    className="!bg-red-50 !text-red-700"
                    onClick={() => setRejectingId(proof.id)}
                >
                    <i className="fas fa-times-circle mr-2 rtl:mr-0 rtl:ml-2"></i>
                    {t("invoices.proof.reject")}
                </TaleButton>
            </div>
        );
    };

    return (
        <div className="mb-6 p-6 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">{t("invoices.proof.title")}</h3>
            {error ? (
                <p className="text-sm text-red-600">{t("invoices.proof.loadError")}</p>
            ) : (
                <PaymentProofHistory proofs={proofs} renderActions={renderActions} />
            )}
        </div>
    );
};
